import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import api from '../../lib/api';
import { toast } from 'react-toastify';

const ConfigurationSMSTab = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    account_sid: '',
    auth_token: '',
    from_number: '',
    configured: false
  });
  
  const [testNumber, setTestNumber] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  const [smsActivations, setSmsActivations] = useState({
    prime_versee: true,
    code_pin: true,
    changement_statut: true,
    reinitialisation_pin: true,
    anniversaire: true
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sms/config');
      setConfig(response.data);
      
      // Charger les activations
      if (response.data.activations) {
        setSmsActivations(response.data.activations);
      }
    } catch (error) {
      console.error('Erreur chargement config SMS:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.account_sid || !config.auth_token || !config.from_number) {
      toast.warning('Tous les champs sont requis');
      return;
    }

    try {
      await api.post('/sms/config', {
        account_sid: config.account_sid,
        auth_token: config.auth_token,
        from_number: config.from_number
      });
      
      toast.success('Configuration SMS sauvegardée avec succès !');
      await loadConfig();
    } catch (error) {
      console.error('Erreur sauvegarde config:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleSendTest = async () => {
    if (!testNumber) {
      toast.warning('Veuillez saisir un numéro de téléphone');
      return;
    }

    try {
      setSendingTest(true);
      await api.post('/sms/test', {
        to_number: testNumber
      });
      
      toast.success('SMS de test envoyé avec succès ! Vérifiez votre téléphone.');
    } catch (error) {
      console.error('Erreur envoi test:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du SMS test');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">📱 Configuration SMS</h2>
        <p className="text-gray-600 mt-1">Gérez les paramètres d'envoi de SMS via Twilio</p>
      </div>

      {/* Configuration Twilio */}
      <Card>
        <CardHeader>
          <CardTitle>🔧 Paramètres Twilio</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Account SID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account SID *
              </label>
              <input
                type="text"
                value={config.account_sid}
                onChange={(e) => setConfig({...config, account_sid: e.target.value})}
                placeholder="AC..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Trouvez-le dans votre Console Twilio → Account Info
              </p>
            </div>

            {/* Auth Token */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auth Token *
              </label>
              <input
                type="password"
                value={config.auth_token}
                onChange={(e) => setConfig({...config, auth_token: e.target.value})}
                placeholder="Votre Auth Token"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Cliquez sur l'œil 👁️ dans votre Console Twilio pour le révéler
              </p>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro Twilio (expéditeur) *
              </label>
              <input
                type="text"
                value={config.from_number}
                onChange={(e) => setConfig({...config, from_number: e.target.value})}
                placeholder="+18157654573"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format international (ex: +18157654573 ou +224621234567)
              </p>
            </div>

            {/* Status */}
            {config.configured && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <span className="text-green-600 mr-2">✅</span>
                  <span className="text-green-800 font-medium">Service SMS configuré et opérationnel</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleSaveConfig}
                className="flex-1 px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium transition-colors"
              >
                💾 Sauvegarder la Configuration
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test SMS */}
      <Card>
        <CardHeader>
          <CardTitle>🧪 Envoyer un SMS de Test</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de téléphone test
              </label>
              <input
                type="text"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="+224621234567"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format international avec + (ex: +224669343535)
              </p>
            </div>

            <button
              onClick={handleSendTest}
              disabled={sendingTest || !config.configured}
              className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
                sendingTest || !config.configured
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {sendingTest ? '📤 Envoi en cours...' : '📱 Envoyer SMS de Test'}
            </button>

            {!config.configured && (
              <p className="text-sm text-orange-600">
                ⚠️ Configurez d'abord Twilio pour envoyer des SMS de test
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Activations par type */}
      <Card>
        <CardHeader>
          <CardTitle>⚙️ Activation des Notifications SMS</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              Activez ou désactivez l'envoi automatique de SMS par type d'événement
            </p>

            {/* Prime versée */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">🎁</span>
                <div>
                  <p className="font-medium text-gray-900">SMS Prime versée</p>
                  <p className="text-xs text-gray-600">Envoyé quand un employé reçoit une prime</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsActivations.prime_versee}
                  onChange={(e) => setSmsActivations({...smsActivations, prime_versee: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>

            {/* Code PIN */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">🔐</span>
                <div>
                  <p className="font-medium text-gray-900">SMS Code PIN</p>
                  <p className="text-xs text-gray-600">Envoyé lors de la création d'un employé</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsActivations.code_pin}
                  onChange={(e) => setSmsActivations({...smsActivations, code_pin: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>

            {/* Changement statut */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <p className="font-medium text-gray-900">SMS Changement Statut</p>
                  <p className="text-xs text-gray-600">Envoyé quand le statut change (suspendu, réactivé, etc.)</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsActivations.changement_statut}
                  onChange={(e) => setSmsActivations({...smsActivations, changement_statut: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>

            {/* Réinitialisation PIN */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">🔄</span>
                <div>
                  <p className="font-medium text-gray-900">SMS Réinitialisation PIN</p>
                  <p className="text-xs text-gray-600">Envoyé lors d'une réinitialisation de code PIN</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsActivations.reinitialisation_pin}
                  onChange={(e) => setSmsActivations({...smsActivations, reinitialisation_pin: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>

            {/* Anniversaire */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">🎂</span>
                <div>
                  <p className="font-medium text-gray-900">SMS Anniversaire</p>
                  <p className="text-xs text-gray-600">Envoyé automatiquement le jour de l'anniversaire</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsActivations.anniversaire}
                  onChange={(e) => setSmsActivations({...smsActivations, anniversaire: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>

            <div className="mt-4 pt-4 border-t">
              <button
                onClick={async () => {
                  try {
                    await api.post('/sms/activations', smsActivations);
                    toast.success('Préférences d\'activation sauvegardées !');
                  } catch (error) {
                    toast.error('Erreur lors de la sauvegarde');
                  }
                }}
                className="w-full px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
              >
                💾 Sauvegarder les Activations
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informations */}
      <Card>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">ℹ️ Informations importantes</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Coût estimé : ~0.08 USD par SMS vers la Guinée</li>
              <li>• Les SMS sont envoyés automatiquement selon les événements activés</li>
              <li>• Tous les SMS incluent la signature "Parce que Nous prévoyons vos Imprévus"</li>
              <li>• L'historique des SMS est disponible dans l'onglet "Historique SMS"</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigurationSMSTab;
